import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { useAuth } from '../context/AuthContext';
import { Logo } from '../components/Logo';
import { 
  Leaf, ArrowLeft, Plus, Clock, Bell, Trash2, 
  Loader2, Moon, Sun, Coffee, Sunset, Check, ChevronDown, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import PushNotificationToggle from '../components/PushNotificationToggle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAYS = [
  { id: 'mon', label: 'M' },
  { id: 'tue', label: 'T' },
  { id: 'wed', label: 'W' },
  { id: 'thu', label: 'T' },
  { id: 'fri', label: 'F' },
  { id: 'sat', label: 'S' },
  { id: 'sun', label: 'S' },
];

const PRESET_TIMES = [
  { time: '08:00', label: 'Morning', icon: Sun },
  { time: '12:00', label: 'Noon', icon: Coffee },
  { time: '18:00', label: 'Evening', icon: Sunset },
  { time: '22:00', label: 'Bedtime', icon: Moon },
];

/**
 * Time-of-day visual palette for reminder cards.
 * Maps a time string ("HH:MM") to a coherent color scheme reflecting
 * the time of day (sunrise → daylight → sunset → twilight).
 */
function timeOfDayStyle(timeStr) {
  const hour = parseInt((timeStr || '08:00').split(':')[0], 10);
  // Morning 5–11: sunrise amber
  if (hour >= 5 && hour < 11) {
    return {
      key: 'morning',
      label: 'Morning',
      borderClass: 'border-amber-200 border-l-4 border-l-amber-500',
      iconBgClass: 'bg-amber-100',
      iconColorClass: 'text-amber-600',
    };
  }
  // Midday 11–17: sky blue
  if (hour >= 11 && hour < 17) {
    return {
      key: 'midday',
      label: 'Midday',
      borderClass: 'border-sky-200 border-l-4 border-l-sky-500',
      iconBgClass: 'bg-sky-100',
      iconColorClass: 'text-sky-600',
    };
  }
  // Evening 17–21: sunset orange-rose
  if (hour >= 17 && hour < 21) {
    return {
      key: 'evening',
      label: 'Evening',
      borderClass: 'border-orange-200 border-l-4 border-l-orange-500',
      iconBgClass: 'bg-orange-100',
      iconColorClass: 'text-orange-600',
    };
  }
  // Night 21–5: indigo/twilight
  return {
    key: 'night',
    label: 'Night',
    borderClass: 'border-indigo-200 border-l-4 border-l-indigo-500',
    iconBgClass: 'bg-indigo-100',
    iconColorClass: 'text-indigo-600',
  };
}

export default function RemindersPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [focusedTime, setFocusedTime] = useState(null);
  const { user, getAuthHeader, loading: authLoading } = useAuth();
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [syncing, setSyncing] = useState(false);
  const [newReminder, setNewReminder] = useState({
    supplement_name: '',
    time: '08:00',
    days: ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'],
    enabled: true
  });
  // Track which supplement names have already been check-in logged today
  // (case-insensitive, name-based — same as the check-in widget).
  const [loggedToday, setLoggedToday] = useState(new Set());
  // Per-time-slot loading state for the "Mark taken" pill
  const [loggingSlot, setLoggingSlot] = useState(null);

  const fetchLoggedToday = async () => {
    try {
      const r = await axios.get(`${API}/checkin/today`, { headers: getAuthHeader() });
      const names = (r.data?.supplements_taken || []).map(n => String(n).toLowerCase());
      setLoggedToday(new Set(names));
    } catch (e) { /* noop */ }
  };

  // Log the supplements of a given time-slot group via the incremental endpoint.
  // Excludes Rx + water (we only log actual supplements toward the streak — same
  // rule used by the dashboard check-in widget).
  const handleLogSlot = async (time, group) => {
    const supplements = group
      .filter(r => r.enabled !== false)
      .filter(r => !r.is_medication)
      .map(r => (r.supplement_name || '').trim())
      .filter(name => {
        if (!name) return false;
        if (name.startsWith('💧')) return false;
        if (/water \(cup/i.test(name)) return false;
        return true;
      });
    if (supplements.length === 0) {
      toast.error('Nothing to log — this slot has no supplements (Rx and water are excluded)');
      return;
    }
    setLoggingSlot(time);
    try {
      const res = await axios.post(
        `${API}/checkin/log-slot`,
        { supplements, time },
        { headers: getAuthHeader() }
      );
      const updated = (res.data?.supplements_today || []).map(n => String(n).toLowerCase());
      setLoggedToday(new Set(updated));
      toast.success(
        res.data?.current_streak > 0
          ? `✓ Logged ${supplements.length} — Streak ${res.data.current_streak} day${res.data.current_streak !== 1 ? 's' : ''}`
          : `✓ Logged ${supplements.length}`
      );
    } catch (e) {
      console.error(e);
      toast.error('Failed to log slot');
    } finally {
      setLoggingSlot(null);
    }
  };

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/auth?redirect=/reminders');
    }
  }, [user, authLoading, navigate]);

  const fetchReminders = async () => {
    if (!user) return;
    try {
      // Auto-deduplicate + sync medication flags from latest plan
      await axios.post(`${API}/reminders/deduplicate`, {}, { headers: getAuthHeader() }).catch(() => {});
      await axios.post(`${API}/reminders/sync-medication-flags`, {}, { headers: getAuthHeader() }).catch(() => {});
      const response = await axios.get(`${API}/reminders`, {
        headers: getAuthHeader()
      });
      setReminders(response.data);
    } catch (error) {
      console.error('Error fetching reminders:', error);
      toast.error('Failed to load reminders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchReminders();
      fetchLoggedToday();
    }
  }, [user]);

  // Deep-link focus: when arriving via `/reminders?focus=HH:MM` (typically from a
  // tapped push notification), scroll to & briefly highlight that time-group card.
  // If `&autolog=1` is also present (from the "✓ Took it" notification action),
  // log that slot incrementally as soon as the page loads.
  useEffect(() => {
    if (loading) return;
    const params = new URLSearchParams(location.search);
    const focus = params.get('focus');
    if (!focus) return;
    // Make sure the group is expanded so the user sees the items
    setExpandedGroups(prev => ({ ...prev, [focus]: true }));
    setFocusedTime(focus);
    // Wait one frame for the DOM to render the group, then scroll into view
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-testid="reminder-group-${focus}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 200);
    // Auto-clear the highlight after 3.5s so the page returns to neutral state
    const clear = setTimeout(() => setFocusedTime(null), 3500);

    // Auto-log slot if the user tapped "Took it" from the notification.
    if (params.get('autolog') === '1') {
      const matchingGroup = reminders.filter(r => r.time === focus);
      if (matchingGroup.length > 0) {
        handleLogSlot(focus, matchingGroup);
      }
      // Strip the autolog param so a refresh doesn't re-fire
      const cleanUrl = `${window.location.pathname}?focus=${encodeURIComponent(focus)}`;
      window.history.replaceState({}, '', cleanUrl);
    }

    return () => { clearTimeout(t); clearTimeout(clear); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, location.search, reminders.length]);

  // Listen for the SW's LOG_SLOT message — fired when the user taps the "✓ Took it"
  // notification action while the PWA is already open. We log the slot silently
  // without navigating away from wherever the user happens to be.
  useEffect(() => {
    if (!user) return;
    const handler = async (event) => {
      const msg = event.data;
      if (!msg || msg.type !== 'LOG_SLOT' || !msg.time || !Array.isArray(msg.supplements)) return;
      try {
        const res = await axios.post(
          `${API}/checkin/log-slot`,
          { supplements: msg.supplements, time: msg.time },
          { headers: getAuthHeader() }
        );
        const updated = (res.data?.supplements_today || []).map(n => String(n).toLowerCase());
        setLoggedToday(new Set(updated));
        toast.success(`✓ Logged ${msg.supplements.length} from ${formatTime(msg.time)}`);
      } catch (e) {
        console.error('LOG_SLOT failed', e);
        toast.error('Failed to log slot');
      }
    };
    navigator.serviceWorker?.addEventListener('message', handler);
    return () => navigator.serviceWorker?.removeEventListener('message', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleAddReminder = async () => {
    if (!newReminder.supplement_name.trim()) {
      toast.error('Please enter a supplement name');
      return;
    }

    try {
      await axios.post(`${API}/reminders`, newReminder, {
        headers: getAuthHeader()
      });
      toast.success('Reminder created!');
      setShowAddDialog(false);
      setNewReminder({
        supplement_name: '',
        time: '08:00',
        days: ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'],
        enabled: true
      });
      fetchReminders();
    } catch (error) {
      console.error('Error creating reminder:', error);
      toast.error('Failed to create reminder');
    }
  };

  const handleToggleReminder = async (reminder) => {
    try {
      await axios.put(`${API}/reminders/${reminder.id}`, {
        ...reminder,
        enabled: !reminder.enabled
      }, {
        headers: getAuthHeader()
      });
      fetchReminders();
    } catch (error) {
      console.error('Error updating reminder:', error);
      toast.error('Failed to update reminder');
    }
  };

  const handleToggleGroup = async (group, enable) => {
    try {
      await Promise.all(group.map(r =>
        axios.put(`${API}/reminders/${r.id}`, { ...r, enabled: enable }, { headers: getAuthHeader() })
      ));
      toast.success(enable ? 'Group enabled' : 'Group disabled');
      fetchReminders();
    } catch (error) {
      toast.error('Failed to update group');
    }
  };

  const handleSyncToPlan = async () => {
    setSyncing(true);
    try {
      const { data } = await axios.post(`${API}/reminders/sync-to-plan`, {}, { headers: getAuthHeader() });
      const bits = [];
      if (data.removed > 0) bits.push(`removed ${data.removed}`);
      if (data.added > 0) bits.push(`added ${data.added}`);
      if (bits.length === 0) bits.push('already up to date');
      toast.success(`Synced — ${bits.join(', ')}`);
      fetchReminders();
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Failed to sync';
      toast.error(msg);
    } finally {
      setSyncing(false);
    }
  };

  const handleBulkTimeChange = async (fromTime, toTime) => {
    if (fromTime === toTime) return;
    try {
      const { data } = await axios.post(
        `${API}/reminders/bulk-update-time`,
        { from_time: fromTime, to_time: toTime },
        { headers: getAuthHeader() }
      );
      toast.success(`Moved ${data.updated} reminder${data.updated !== 1 ? 's' : ''} to ${toTime}`);
      fetchReminders();
    } catch (error) {
      toast.error('Failed to update time');
    }
  };

  const handleDeleteReminder = async (reminderId) => {
    try {
      await axios.delete(`${API}/reminders/${reminderId}`, {
        headers: getAuthHeader()
      });
      toast.success('Reminder deleted');
      fetchReminders();
    } catch (error) {
      console.error('Error deleting reminder:', error);
      toast.error('Failed to delete reminder');
    }
  };

  const toggleDay = (day) => {
    setNewReminder(prev => ({
      ...prev,
      days: prev.days.includes(day)
        ? prev.days.filter(d => d !== day)
        : [...prev.days, day]
    }));
  };

  const handleUpdateTime = async (reminder, newTime) => {
    try {
      await axios.put(`${API}/reminders/${reminder.id}`, {
        ...reminder,
        time: newTime
      }, { headers: getAuthHeader() });
      fetchReminders();
      toast.success(`${reminder.supplement_name} updated to ${formatTime(newTime)}`);
    } catch (error) {
      toast.error('Failed to update time');
    }
  };

  const formatTime = (time) => {
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${minutes} ${ampm}`;
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Header */}
      <header className="border-b border-border bg-white sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <button 
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </button>
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-[#1A1A1A]"
          >
            <Logo size="default" linkTo={null} className="pointer-events-none" />
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Page Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-[#1A1A1A] mb-2 flex items-center gap-3">
                <Bell className="w-7 h-7 text-primary" />
                Supplement Reminders
              </h1>
              <p className="text-sm text-muted-foreground">
                Set reminders to stay consistent with your supplement routine.
              </p>
            </div>
            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild>
                <Button className="btn-primary text-sm">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Reminder
                </Button>
              </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Create New Reminder</DialogTitle>
                </DialogHeader>
                <div className="space-y-6 pt-4">
                  {/* Supplement Name */}
                  <div className="space-y-2">
                    <Label>Supplement Name</Label>
                    <Input
                      placeholder="e.g., Vitamin D3, Omega-3..."
                      value={newReminder.supplement_name}
                      onChange={(e) => setNewReminder(prev => ({ ...prev, supplement_name: e.target.value }))}
                    />
                  </div>

                  {/* Time Selection */}
                  <div className="space-y-3">
                    <Label>Time</Label>
                    <div className="grid grid-cols-4 gap-2">
                      {PRESET_TIMES.map(({ time, label, icon: Icon }) => (
                        <button
                          key={time}
                          onClick={() => setNewReminder(prev => ({ ...prev, time }))}
                          className={`p-3 rounded-lg border text-center transition-all ${
                            newReminder.time === time
                              ? 'border-primary bg-primary/10 text-primary'
                              : 'border-border hover:border-primary/30'
                          }`}
                        >
                          <Icon className="w-5 h-5 mx-auto mb-1" />
                          <span className="text-xs font-medium">{label}</span>
                        </button>
                      ))}
                    </div>
                    <Input
                      type="time"
                      value={newReminder.time}
                      onChange={(e) => setNewReminder(prev => ({ ...prev, time: e.target.value }))}
                      className="mt-2"
                    />
                  </div>

                  {/* Days Selection */}
                  <div className="space-y-3">
                    <Label>Days</Label>
                    <div className="flex gap-2">
                      {DAYS.map(({ id, label }) => (
                        <button
                          key={id}
                          onClick={() => toggleDay(id)}
                          className={`w-10 h-10 rounded-full flex items-center justify-center font-medium text-sm transition-all ${
                            newReminder.days.includes(id)
                              ? 'bg-primary text-white'
                              : 'bg-muted text-muted-foreground hover:bg-muted/80'
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <Button onClick={handleAddReminder} className="w-full btn-primary">
                    Create Reminder
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </motion.div>

        {/* Push Notifications Toggle */}
        <div className="mb-4 p-4 bg-white rounded-xl border border-border/50">
          <PushNotificationToggle />
        </div>

        {/* Sync to current plan */}
        {reminders.length > 0 && (
          <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-3" data-testid="sync-to-plan-card">
            <RefreshCw className="w-5 h-5 text-amber-700 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-[#1A1A1A]">Sync to current plan</p>
              <p className="text-xs text-muted-foreground">Removes old reminders and adds anything new from your latest assessment.</p>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={handleSyncToPlan}
              disabled={syncing}
              className="border-amber-300 text-amber-700 hover:bg-amber-100 flex-shrink-0"
              data-testid="sync-to-plan-btn"
            >
              {syncing ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Sync'}
            </Button>
          </div>
        )}

        {/* Color Legend */}
        {reminders.some(r => r.is_medication) && (
          <div className="mb-6 flex items-center gap-4 px-1 text-xs text-muted-foreground" data-testid="reminders-legend">
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-primary" />
              Supplement
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-red-600" />
              Medication (Rx)
            </span>
          </div>
        )}

        {/* Reminders List */}
        {reminders.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-center py-16 bg-white rounded-2xl border border-border/50"
          >
            <Clock className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium text-[#1A1A1A] mb-2">No reminders yet</h3>
            <p className="text-muted-foreground mb-6">
              Create reminders to help you stay on track with your supplements.
            </p>
            <Button onClick={() => setShowAddDialog(true)} className="btn-primary">
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Reminder
            </Button>
          </motion.div>
        ) : (
          <div className="space-y-3" data-testid="reminders-list">
            {(() => {
              // Group reminders by time
              const groups = {};
              [...reminders].sort((a, b) => a.time.localeCompare(b.time)).forEach(r => {
                if (!groups[r.time]) groups[r.time] = [];
                groups[r.time].push(r);
              });
              const times = Object.keys(groups);

              return times.map((time, gIndex) => {
                const group = groups[time];
                const allEnabled = group.every(r => r.enabled);
                const someEnabled = group.some(r => r.enabled);
                const isExpanded = expandedGroups[time] !== false; // default expanded
                const tod = timeOfDayStyle(time);
                // Logged-state for this slot — only counts supplement items
                // (Rx + water excluded), matching what handleLogSlot submits.
                const loggable = group
                  .filter(r => r.enabled !== false && !r.is_medication)
                  .map(r => (r.supplement_name || '').trim())
                  .filter(n => n && !n.startsWith('💧') && !/water \(cup/i.test(n));
                const allLogged = loggable.length > 0 && loggable.every(n => loggedToday.has(n.toLowerCase()));
                return (
                  <motion.div
                    key={time}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.05 + gIndex * 0.05 }}
                    className={`bg-white rounded-xl border overflow-hidden transition-all duration-500 ${tod.borderClass} ${
                      focusedTime === time ? 'ring-4 ring-primary/40 shadow-lg shadow-primary/20 scale-[1.01]' : ''
                    }`}
                    data-testid={`reminder-group-${time}`}
                    data-tod={tod.key}
                    data-focused={focusedTime === time ? 'true' : 'false'}
                  >
                    {/* Group Header */}
                    <div className="p-4 flex items-center gap-3">
                      <button
                        onClick={() => setExpandedGroups(prev => ({ ...prev, [time]: !isExpanded }))}
                        className="flex-1 flex items-center gap-3 min-w-0 text-left"
                        data-testid={`group-toggle-${time}`}
                      >
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                          someEnabled ? tod.iconBgClass : 'bg-muted'
                        }`}>
                          <Clock className={`w-5 h-5 ${
                            someEnabled ? tod.iconColorClass : 'text-muted-foreground'
                          }`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline gap-2 flex-wrap">
                            <input
                              type="time"
                              value={time}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => handleBulkTimeChange(time, e.target.value)}
                              className="font-semibold text-base text-[#1A1A1A] bg-transparent border-none p-0 cursor-pointer hover:text-primary focus:outline-none focus:text-primary w-[120px]"
                              data-testid={`group-time-${time}`}
                              title="Tap to change time for all items in this group"
                            />
                            <span className="text-xs text-muted-foreground">
                              {group.length} item{group.length !== 1 ? 's' : ''}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground truncate">
                            {group.map(r => r.supplement_name).join(', ')}
                          </p>
                        </div>
                        <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                      </button>
                      <Switch
                        checked={allEnabled}
                        onCheckedChange={() => handleToggleGroup(group, !allEnabled)}
                        data-testid={`group-switch-${time}`}
                      />
                    </div>

                    {/* "Mark taken" pill — incremental check-in for this slot */}
                    {loggable.length > 0 && (
                      <div className="px-4 pb-3 -mt-1">
                        <button
                          type="button"
                          onClick={() => handleLogSlot(time, group)}
                          disabled={loggingSlot === time || allLogged}
                          className={`w-full flex items-center justify-center gap-1.5 text-xs font-medium rounded-full px-3 py-2 transition ${
                            allLogged
                              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 cursor-default'
                              : 'bg-emerald-600 text-white hover:bg-emerald-700 active:scale-95'
                          }`}
                          data-testid={`mark-taken-${time}`}
                        >
                          {loggingSlot === time ? (
                            <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Logging…</>
                          ) : allLogged ? (
                            <><Check className="w-3.5 h-3.5" /> Logged today</>
                          ) : (
                            <><Check className="w-3.5 h-3.5" /> Mark taken ({loggable.length})</>
                          )}
                        </button>
                      </div>
                    )}

                    {/* Group Items */}
                    {isExpanded && (
                      <div className="border-t border-border/40 bg-muted/20">
                        {group.map((reminder) => (
                          <div
                            key={reminder.id}
                            className={`px-4 py-3 flex items-center gap-3 border-b border-border/30 last:border-b-0 ${
                              !reminder.enabled ? 'opacity-50' : ''
                            }`}
                            data-testid={`reminder-row-${reminder.id}`}
                          >
                            <Bell className={`w-4 h-4 flex-shrink-0 ${
                              !reminder.enabled
                                ? 'text-muted-foreground'
                                : reminder.is_medication
                                  ? 'text-red-600'
                                  : 'text-primary'
                            }`} />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm font-medium text-[#1A1A1A] truncate">{reminder.supplement_name}</span>
                                {reminder.is_medication && (
                                  <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-red-100 text-red-700 border border-red-200 flex-shrink-0" data-testid={`rx-badge-${reminder.id}`}>
                                    Rx
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-2 mt-0.5">
                                <input
                                  type="time"
                                  value={reminder.time}
                                  onChange={(e) => handleUpdateTime(reminder, e.target.value)}
                                  className="text-[11px] font-medium text-muted-foreground bg-transparent border-none p-0 w-[95px] cursor-pointer focus:outline-none focus:ring-0"
                                  data-testid={`reminder-time-${reminder.id}`}
                                />
                                <span className={`text-[11px] ${reminder.days?.length === 7 ? 'text-muted-foreground' : 'text-amber-600 font-medium'}`}>
                                  {reminder.days?.length === 7
                                    ? 'Daily'
                                    : reminder.days?.length > 0
                                      ? reminder.days.map(d => d.charAt(0).toUpperCase()).join(' ') + ' only'
                                      : 'No days set ⚠'}
                                </span>
                              </div>
                            </div>
                            <Switch
                              checked={reminder.enabled}
                              onCheckedChange={() => handleToggleReminder(reminder)}
                              className="scale-75"
                            />
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteReminder(reminder.id)}
                              className="text-muted-foreground hover:text-destructive h-7 w-7"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </motion.div>
                );
              });
            })()}
          </div>
        )}
      </main>
    </div>
  );
}
